/**
 * The reach feature (M3 plan R12, order `FEATURE_ORDER.reach`, reach mode only): the per-tick heartbeat of the runtime
 * and the sun.
 *
 * - `init`: registers every subsystem's `controlFilter` into `ctx.controlFilters` (wrapped with the core), and follows
 *   quality changes so the streamed rocks regenerate at the new preset.
 * - `beforeTicks`: on a held frame (docked: the loop runs no ticks, so no `afterTick`) it runs every subsystem's
 *   `held` hook, so the local traffic keeps reporting to the strategic core whose clock runs on (D-419: held traffic).
 * - `afterTick`: reads M3's input (`readReachInput`; suppressed while `ctx.ui.modal` > 0, so nothing held through a
 *   panel fires on close), then steps the runtime (`ctx.reach.step(ctx.sim)`).
 * - `frame`: `env.setSunDirection` toward the star (the system origin: −camera position, normalised). Inside a gas
 *   field it owns the dust haze (the field's centre in render space, its radius); the field feature, which runs after,
 *   overrides it wherever rocks are drawn, and the haze is cleared once on leaving the gas. With no rocks and no gas
 *   the haze is cleared once too (R20): the field feature leaves its last haze in place when `sim.field` goes away, and
 *   in render space (camera-relative) that stale sphere would fog every rock-less system after a jump.
 *
 * On the first frame (R20) it starts the one-off pipeline warm-up of every ship def and the effects (warm.ts), which
 * compiles in the first frames' render passes.
 *
 * Test builds (R20): `init` installs `window.__reachDebug` (src/reach/debug.ts, its own chunk), and `afterTick` merges
 * the API's held and pressed inputs after the device read.
 *
 * `beforeTicks`, `afterTick` and `frame` allocate nothing (D-014).
 */
import { Vector3 } from 'three/webgpu'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import type { FieldSite } from '../../galaxy/layout/types'
import { loadReachControls, type ReachControls } from '../../reach/controls'
import { readReachInput, suppressReachInput } from '../../reach/input'
import type { ReachRuntime } from '../../reach/runtime/types'
import type { QualityPreset } from '../../render/contracts'
import { toRenderSpace } from '../../render/floatingOrigin'
import type { ReachDebugInput } from '../../reach/debug'
import fxFeature from '../fx/feature'
import reachFxFeature from '../reachFx/feature'
import { warmReach } from './warm'

const IS_TEST = import.meta.env.MODE === 'test'

/** The runtime's optional preset hook (the browser runtime has it; a bare `ReachRuntime` may not). */
interface PresetAware {
  setPreset(preset: QualityPreset): void
}

function presetAware(reach: ReachRuntime): PresetAware | null {
  const candidate = reach as Partial<PresetAware>
  return typeof candidate.setPreset === 'function' ? (reach as ReachRuntime & PresetAware) : null
}

/** The gas field whose sphere holds (x, y, z), or null. Allocation-free. */
export function gasFieldAt(fields: readonly FieldSite[], x: number, y: number, z: number): FieldSite | null {
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]
    if (f.kind !== 'gas') continue
    const dx = x - f.center[0]
    const dy = y - f.center[1]
    const dz = z - f.center[2]
    if (dx * dx + dy * dy + dz * dz <= f.radiusM * f.radiusM) return f
  }
  return null
}

export interface ReachFeature extends GameFeature {
  /** True while this feature, not the field feature, set the dust haze (inside a gas field). */
  readonly ownsDust: boolean
  beforeTicks(ctx: GameContext, f: FrameInfo): void
  afterTick(ctx: GameContext): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export interface ReachFeatureOptions {
  /** The one-off warm-up started on the first frame (default none; the game's feature warms ships and effects). */
  warm?: ((ctx: GameContext) => void) | null
}

export function createReachFeature(controls: ReachControls = loadReachControls(), options: ReachFeatureOptions = {}): ReachFeature {
  const warm = options.warm ?? null
  let warmPending = false
  const dust = new Vector3()
  let ownsDust = false
  /** `sim.field` was non-null last frame (the field feature was setting the haze). */
  let hadField = true
  let offQuality: (() => void) | null = null
  let debugInput: ReachDebugInput | null = null
  let applyDebug: ((o: ReachDebugInput, input: ReachRuntime['input'], modal: boolean) => void) | null = null

  const feature: ReachFeature = {
    name: 'reach',
    order: FEATURE_ORDER.reach,
    get ownsDust(): boolean {
      return ownsDust
    },

    init(ctx: GameContext): void | Promise<void> {
      const reach = ctx.reach
      if (reach === null) return
      for (const s of reach.subsystems) {
        const filter = s.controlFilter
        if (filter !== undefined) ctx.controlFilters.push((c) => filter(reach, c))
      }
      const aware = presetAware(reach)
      if (aware !== null) {
        aware.setPreset(ctx.quality)
        offQuality = ctx.bus.on('quality', (preset) => aware.setPreset(preset))
      }
      warmPending = warm !== null
      if (IS_TEST && typeof window !== 'undefined') return installDebug(ctx, reach)
    },

    beforeTicks(ctx: GameContext, f: FrameInfo): void {
      const reach = ctx.reach
      if (reach === null || f.held !== true) return
      const subs = reach.subsystems
      for (let i = 0; i < subs.length; i++) subs[i].held?.(reach, ctx.sim)
    },

    afterTick(ctx: GameContext): void {
      const reach = ctx.reach
      if (reach === null) return
      const modal = ctx.ui.modal > 0
      if (modal) suppressReachInput(ctx.input, controls, reach.input)
      else readReachInput(ctx.input, controls, SIM_DT, reach.input)
      if (debugInput !== null && applyDebug !== null) applyDebug(debugInput, reach.input, modal)
      reach.step(ctx.sim)
    },

    frame(ctx: GameContext): void {
      const reach = ctx.reach
      if (reach === null) return
      if (warmPending) {
        warmPending = false
        warm?.(ctx)
      }
      const cam = ctx.rig.pos
      const d = Math.sqrt(cam.x * cam.x + cam.y * cam.y + cam.z * cam.z)
      if (d > 0) ctx.env.setSunDirection(-cam.x / d, -cam.y / d, -cam.z / d)
      const layout = reach.session.layout
      const gas = layout === null ? null : gasFieldAt(layout.fields, cam.x, cam.y, cam.z)
      if (gas !== null) {
        toRenderSpace(gas.center[0], gas.center[1], gas.center[2], cam, dust)
        ctx.env.setDustVolume(dust, gas.radiusM)
        ownsDust = true
      } else if (ownsDust || (hadField && ctx.sim.field === null)) {
        ctx.env.setDustVolume(dust.set(0, 0, 0), 0)
        ownsDust = false
      }
      hadField = ctx.sim.field !== null
    },

    dispose(): void {
      offQuality?.()
      offQuality = null
      ownsDust = false
      hadField = true
      warmPending = false
      debugInput = null
    },
  }

  /** Test builds: installs `window.__reachDebug` over the running game (the debug module is its own chunk). */
  async function installDebug(ctx: GameContext, reach: ReachRuntime): Promise<void> {
    try {
      const debug = await import('../../reach/debug')
      if (!debug.isHeadlessCore(reach)) {
        logger.warn('reach: __reachDebug needs the full reach runtime; not installed')
        return
      }
      applyDebug = debug.applyDebugInput
      debugInput = await debug.installReachDebug(ctx, reach, () => feature.afterTick(ctx))
    } catch (err: unknown) {
      logger.error('reach: __reachDebug failed to install', err)
    }
  }

  return feature
}

const feature: ReachFeature = createReachFeature(loadReachControls(), {
  warm: (ctx) => void warmReach(ctx, fxFeature, reachFxFeature),
})
export default feature
